import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import styles from './styles/styles.module.css';

const FormInput = forwardRef(function FormInput(
  {
    label,
    inputName,
    placeholderText,
    smallSize,
    validationRules,
    disabled,
    isDatePicker,
    isTextarea,
    onValueChange,
    inputType,
    minDate,
    includesDate,
  },
  ref
) {
  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const wrapperRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  console.log('min date', minDate);

  const fieldValue = useWatch({
    control,
    name: inputName,
  });

  const error = errors[inputName];

  const clearInput = () => {
    setValue(inputName, '');
  };

  /** закрытие подсказок адреса */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <label
          htmlFor={inputName}
          className={`${styles.inputLabel} ${error ? styles.inputLabel_error : ''}`}
        >
          {label}
        </label>

        {/* DATE PICKER */}
        {isDatePicker && (
          <Controller
            name={inputName}
            control={control}
            rules={validationRules}
            render={({ field }) => (
              <CustomDatePicker
                includesDate={includesDate}
                field={field}
                inputName={inputName}
                placeholderText={placeholderText}
                error={error}
                disabled={disabled}
                minDate={minDate}
              />
            )}
          />
        )}

        {/* TEXTAREA */}
        {isTextarea && (
          <textarea
            {...register(inputName, validationRules)}
            className={`
              ${styles.textarea} 
              ${error ? styles.input_error : ''} 
              ${disabled ? styles.disabledTextArea : ''}`}
            placeholder={placeholderText}
            disabled={disabled}
            onFocus={() => clearErrors(inputName)}
          />
        )}

        {/* INPUT */}
        {!isTextarea && !isDatePicker && (
          <Controller
            disabled={disabled}
            name={inputName}
            control={control}
            rules={validationRules}
            render={({ field }) => (
              <div ref={wrapperRef} style={{ position: 'relative' }}>
                <input
                  {...field}
                  ref={ref}
                  // onBlur={() => {
                  //   if (inputName === 'fullAddress' || inputName === 'full_address') {
                  //     const value = field.value || '';

                  //     const isFromSuggestions = suggestions.some(
                  //       (sug) => sug.value === value
                  //     );
                  //   }
                  // }}
                  type={
                    inputName === 'code' || inputName === 'phoneNumber'
                      ? 'tel'
                      : inputType
                  }
                  id={inputName}
                  disabled={disabled}
                  placeholder={placeholderText}
                  className={`
                    ${styles.input}
                    ${smallSize ? styles.input_small : ''}
                    ${error ? styles.input_error : ''}
                  `}
                  value={field.value ?? ''}
                  onFocus={() => clearErrors(inputName)}
                  onChange={async (e) => {
                    const { value } = e.target;

                    /* ADDRESS */
                    if (inputName === 'fullAddress' || inputName === 'full_address') {
                      field.onChange(value);

                      if (value.length > 3) {
                        try {
                          const res = await fetch(
                            'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/address',
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Accept: 'application/json',
                                Authorization:
                                  'Token f1e06ba8aada92eaba4f09baf4e85d1a1c5440e3',
                              },
                              body: JSON.stringify({ query: value }),
                            }
                          );

                          const data = await res.json();
                          setSuggestions(data?.suggestions || []);
                        } catch {
                          setSuggestions([]);
                        }
                      } else {
                        setSuggestions([]);
                      }
                      return;
                    }

                    /* EMAIL */
                    if (inputName === 'email' || inputName === 'Email') {
                      const filtered = value.replace(/[^a-zA-Z0-9@._-]/g, '');
                      field.onChange(filtered);
                      onValueChange?.(filtered);
                      return;
                    }

                    /* PHONE */
                    if (inputName === 'phone') {
                      let digits = value.replace(/\D/g, '');
                      if (digits.startsWith('7')) digits = digits.slice(1);
                      digits = digits.slice(0, 10);

                      let formatted = '+7';
                      if (digits.length > 0) formatted += ` (${digits.slice(0, 3)}`;
                      if (digits.length >= 4) formatted += `) ${digits.slice(3, 6)}`;
                      if (digits.length >= 7) formatted += `-${digits.slice(6, 8)}`;
                      if (digits.length >= 9) formatted += `-${digits.slice(8, 10)}`;

                      field.onChange(formatted);
                      onValueChange?.(formatted);
                      return;
                    }

                    /* CARD NUMBER */
                    if (inputName === 'num_bankCard') {
                      const clean = value.replace(/\D/g, '').slice(0, 16);
                      const formatted = clean.match(/.{1,4}/g)?.join(' ') || '';
                      field.onChange(formatted);
                      onValueChange?.(formatted);
                      return;
                    }

                    /* CARD DATE */
                    if (inputName === 'card_valPeriod') {
                      const clean = value.replace(/\D/g, '').slice(0, 4);
                      const formatted =
                        clean.length > 2
                          ? `${clean.slice(0, 2)}/${clean.slice(2)}`
                          : clean;
                      field.onChange(formatted);
                      onValueChange?.(formatted);
                      return;
                    }

                    /* CVV */
                    if (inputName === 'card_cvv') {
                      const clean = value.replace(/\D/g, '').slice(0, 3);
                      field.onChange(clean);
                      onValueChange?.(clean);
                      return;
                    }

                    /* DEFAULT */
                    field.onChange(
                      inputType === 'number'
                        ? value === ''
                          ? null
                          : Number(value)
                        : value
                    );
                    onValueChange?.(value);
                  }}
                  onKeyDown={(e) => {
                    if (suggestions.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setHighlightedIndex((prev) =>
                          prev < suggestions.length - 1 ? prev + 1 : 0
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setHighlightedIndex((prev) =>
                          prev > 0 ? prev - 1 : suggestions.length - 1
                        );
                      } else if (e.key === 'Enter' && highlightedIndex >= 0) {
                        e.preventDefault();
                        clearErrors(inputName);
                        field.onChange(suggestions[highlightedIndex].value);
                        setSuggestions([]);
                        setHighlightedIndex(-1);
                      }
                    }
                  }}
                />

                {suggestions.length > 0 && (
                  <ul
                    className={styles.input_address}
                    style={{
                      position: 'absolute',
                      background: '#fff',
                      border: '1px solid #ccc',
                      width: '100%',
                      listStyle: 'none',
                      margin: 0,
                      padding: 0,
                      zIndex: 100,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      marginTop: '8px',
                    }}
                  >
                    {suggestions.map((sug, index) => (
                      <li
                        style={{
                          padding: '8px',
                          cursor: 'pointer',
                          backgroundColor:
                            index === highlightedIndex ? '#f0f0f0' : 'transparent',
                        }}
                        key={sug.value}
                        className={
                          index === highlightedIndex ? styles.input_address_active : ''
                        }
                        onClick={() => {
                          const { value } = sug;
                          field.onChange(value);
                          setSuggestions([]);
                          setHighlightedIndex(-1);
                        }}
                      >
                        {sug.value}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          />
        )}

        {!!fieldValue && !disabled && !isDatePicker && !isTextarea && (
          <button type="button" className={styles.clearButton} onClick={clearInput} />
        )}
      </div>

      {error && <p className={styles.inputError_text}>{error.message}</p>}
    </div>
  );
});

export default FormInput;

FormInput.propTypes = {
  label: PropTypes.string.isRequired,
  inputName: PropTypes.string.isRequired,
  placeholderText: PropTypes.string,
  smallSize: PropTypes.bool,
  validationRules: PropTypes.shape({}),
  includesDate: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  isDatePicker: PropTypes.bool,
  isTextarea: PropTypes.bool,
  onValueChange: PropTypes.func,
  inputType: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
};
